import { useEffect, useState, useCallback, useRef } from "react";
import { useGetStudentVocabularyWordsQuery } from "@/store/api/user/exerciseApi";

const VOCABULARY_PAGE_SIZE = 20;

const useVocabularyPagination = ({ exerciseId, selectedDay }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [vocabularyPage, setVocabularyPage] = useState(1);
  const [allVocabularyWords, setAllVocabularyWords] = useState([]);
  const [loadedPages, setLoadedPages] = useState(new Set());
  const [vocabularyTotal, setVocabularyTotal] = useState(0);
  const [hasNextVocabularyPage, setHasNextVocabularyPage] = useState(false);
  const pendingAdvanceRef = useRef(false);

  // Fetch vocabulary words with pagination
  const { data: vocabularyResponse, isFetching: isVocabularyFetching } = useGetStudentVocabularyWordsQuery(
    exerciseId && selectedDay
      ? { exerciseId, dayId: selectedDay._id, page: vocabularyPage, limit: VOCABULARY_PAGE_SIZE }
      : undefined,
    { skip: !exerciseId || !selectedDay || loadedPages.has(vocabularyPage) }
  );

  // Append new page data to flat array when response arrives
  useEffect(() => {
    if (vocabularyResponse?.data?.vocabularyItems && vocabularyResponse?.pagination) {
      const responsePage = vocabularyResponse.pagination.page;
      setLoadedPages(prev => {
        if (prev.has(responsePage)) return prev;
        const next = new Set(prev);
        next.add(responsePage);
        return next;
      });
      const startIndex = (responsePage - 1) * VOCABULARY_PAGE_SIZE;
      const itemCount = vocabularyResponse.data.vocabularyItems.length;
      setAllVocabularyWords(prev => {
        const newWords = [...prev];
        vocabularyResponse.data.vocabularyItems.forEach((word, i) => {
          newWords[startIndex + i] = word;
        });
        return newWords;
      });
      setVocabularyTotal(vocabularyResponse.pagination.total);
      setHasNextVocabularyPage(vocabularyResponse.pagination.hasNextPage);

      // If user was waiting to advance, move the slide now that data is available
      if (pendingAdvanceRef.current) {
        pendingAdvanceRef.current = false;
        setCurrentSlide((prev) => {
          const newLength = startIndex + itemCount;
          return prev < newLength - 1 ? prev + 1 : prev;
        });
      }
    }
  }, [vocabularyResponse]);

  // Reset when day changes
  useEffect(() => {
    setAllVocabularyWords([]);
    setLoadedPages(new Set());
    setVocabularyPage(1);
    setVocabularyTotal(0);
    setHasNextVocabularyPage(false);
    setCurrentSlide(0);
  }, [selectedDay?._id]);

  // Handler to load next page — guards against double-increment
  const handleLoadNextPage = useCallback(() => {
    if (!hasNextVocabularyPage) return;
    setVocabularyPage(prev => {
      const maxLoaded = loadedPages.size > 0 ? Math.max(...loadedPages) : 0;
      if (prev <= maxLoaded) return prev + 1;
      return prev;
    });
  }, [hasNextVocabularyPage, loadedPages]);

  // Preload next page when user is near the end of loaded words
  useEffect(() => {
    if (allVocabularyWords.length > 0 && currentSlide >= allVocabularyWords.length - 3 && hasNextVocabularyPage && !isVocabularyFetching) {
      handleLoadNextPage();
    }
  }, [currentSlide, allVocabularyWords.length, hasNextVocabularyPage, handleLoadNextPage, isVocabularyFetching]);

  const totalWords = vocabularyTotal || allVocabularyWords.length;
  const currentWord = allVocabularyWords[currentSlide];
  const canGoNext = currentSlide < allVocabularyWords.length - 1 || hasNextVocabularyPage;
  const canGoPrev = currentSlide > 0;

  const handleNextWord = useCallback(() => {
    if (currentSlide < allVocabularyWords.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (hasNextVocabularyPage) {
      // Mark that user wants to advance; data effect will handle slide move
      pendingAdvanceRef.current = true;
      if (!isVocabularyFetching) {
        handleLoadNextPage();
      }
    }
  }, [currentSlide, allVocabularyWords.length, hasNextVocabularyPage, isVocabularyFetching, handleLoadNextPage]);

  const handlePrevWord = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide]);

  return {
    vocabularyWords: allVocabularyWords,
    currentWord,
    currentSlide,
    totalWords,
    isLoading: isVocabularyFetching && allVocabularyWords.length === 0,
    isFetching: isVocabularyFetching,
    canGoNext,
    canGoPrev,
    handleNextWord,
    handlePrevWord,
  };
};

export default useVocabularyPagination;
